import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { BannerConfig, BannerService } from '../services/banner.service';
import { SolBannerLevel, SolBannerPlacement } from '../models/core/sol-banner';

export interface SolBannerHandlerConfig {
  /** Names this feature's slice of the shared surface. Two features publishing under different owners never clash. */
  readonly owner: string;
  readonly placement?: SolBannerPlacement;
  /** Which surface carries this feature's banners: the app strip above the navigation, or the page canvas. */
  readonly level?: SolBannerLevel;
  /** Receives the key of one of this owner's banners when the **user** closes it, never when `remove` does. */
  readonly closed?: (key: string) => void;
  /** Receives the key of one of this owner's banners when the user presses its action. */
  readonly actionPressed?: (key: string) => void;
}

/**
 * A feature's own slice of the app-shell banner surface.
 *
 * Construct it as a field, so it captures the component's `DestroyRef` from the constructor's
 * injection context: the owner is named once instead of at every call, the callbacks only ever see
 * this owner's banners, and the slice is cleared when the component goes away. A feature that must
 * outlive a page constructs it in a root service instead.
 *
 * An owner belongs to exactly one handler. Two handlers sharing one owner would publish into the same
 * slice and clear each other's banners when either is destroyed.
 *
 * Level says which surface carries this feature's banners: the default `app` is the shell strip above the
 * navigation, `page` is the page canvas. Placement says where the app strip sits: `overlay` covers the app
 * and always carries a close affordance, and the default `inline` pushes the app down and leaves closing to
 * each banner's `dismissible`.
 *
 * Publishing replaces this owner's banners and leaves every other owner's standing. Placement is named once
 * here alongside the owner rather than at every call, since where a feature's banners belong is the
 * feature's own property; the shared strip has one position, so another owner needing the app pushed down
 * settles it for everyone.
 */
export class SolBannerHandler {
  private readonly bannerService: BannerService;
  private readonly owner: string;
  private readonly placement: SolBannerPlacement | undefined;
  private readonly level: SolBannerLevel | undefined;

  constructor(config: SolBannerHandlerConfig) {
    this.bannerService = inject(BannerService);
    this.owner = config.owner;
    this.placement = config.placement;
    this.level = config.level;
    const destroyRef = inject(DestroyRef);

    const closed = config.closed;
    if (closed) {
      this.bannerService.closed$
        .pipe(
          filter(event => event.owner === this.owner),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(event => closed(event.key));
    }

    const actionPressed = config.actionPressed;
    if (actionPressed) {
      this.bannerService.actionPressed$
        .pipe(
          filter(event => event.owner === this.owner),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(event => actionPressed(event.key));
    }

    destroyRef.onDestroy(() => this.clear());
  }

  public publish(configs: readonly BannerConfig[]): void {
    this.bannerService.publish(this.owner, configs, this.placement, this.level);
  }

  public remove(key: string): void {
    this.bannerService.remove(this.owner, key);
  }

  public clear(): void {
    this.bannerService.clear(this.owner);
  }
}
